<script setup>
import { ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const labels = ref([]);
const newLabel = ref('');
const isSaving = ref(false);

watch(
  () => props.inbox.borderchat_labels,
  value => {
    labels.value = Array.isArray(value) ? [...value] : [];
  },
  { immediate: true }
);

const addLabel = () => {
  const label = newLabel.value.trim();
  if (!label || labels.value.includes(label)) {
    newLabel.value = '';
    return;
  }
  if (labels.value.length >= 20) {
    useAlert('最多可添加 20 个标签。');
    return;
  }
  labels.value.push(label);
  newLabel.value = '';
};

const removeLabel = label => {
  labels.value = labels.value.filter(item => item !== label);
};

const saveLabels = async () => {
  try {
    isSaving.value = true;
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      borderchat_labels: labels.value,
    });
    useAlert('收件箱标签已保存。');
  } catch {
    useAlert('保存收件箱标签失败。');
  } finally {
    isSaving.value = false;
  }
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<template>
  <section class="mb-6 rounded-xl border border-n-weak bg-n-alpha-1 p-5">
    <h3 class="text-heading-3 text-n-slate-12">收件箱标签</h3>
    <p class="mb-4 mt-1 text-sm text-n-slate-11">
      添加“亚马逊邮箱”后，新收到的邮件会启用 Amazon 客诉解析。
    </p>

    <div class="mb-4 flex min-h-8 flex-wrap gap-2">
      <span
        v-for="label in labels"
        :key="label"
        class="inline-flex items-center gap-1 rounded-full bg-n-alpha-2 px-3 py-1 text-sm text-n-slate-12"
      >
        {{ label }}
        <button
          type="button"
          class="text-n-slate-10 hover:text-n-slate-12"
          :aria-label="`删除标签 ${label}`"
          @click="removeLabel(label)"
        >
          ×
        </button>
      </span>
      <span v-if="!labels.length" class="text-sm text-n-slate-10">
        尚未添加标签
      </span>
    </div>

    <div class="flex flex-col gap-2 sm:flex-row">
      <input
        v-model="newLabel"
        type="text"
        maxlength="64"
        placeholder="例如：亚马逊邮箱"
        class="h-10 flex-1 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-n-slate-12"
        @keydown.enter.prevent="addLabel"
      />
      <Button type="button" slate outline label="添加" @click="addLabel" />
      <Button
        type="button"
        label="保存标签"
        :is-loading="isSaving"
        @click="saveLabels"
      />
    </div>
  </section>
</template>
